import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
    ArrowUpRight,
    BarChart3,
    Boxes,
    IndianRupee,
    Package,
    ShoppingCart,
    TrendingUp,
    Users,
} from "lucide-react";
import {
    Area,
    AreaChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { adminApi, type AdminStats } from "@/features/admin/api";
import { ROUTES } from "@/constants/routes";

const STATUS_COLORS: Record<string, string> = {
    placed: "bg-slate-500/10 text-slate-700",
    confirmed: "bg-sky-500/10 text-sky-700",
    packing: "bg-amber-500/10 text-amber-700",
    out_for_delivery: "bg-violet-500/10 text-violet-700",
    delivered: "bg-emerald-500/10 text-emerald-700",
    cancelled: "bg-rose-500/10 text-rose-700",
};

function StatCard({
    label,
    value,
    hint,
    icon: Icon,
    tone,
    testId,
}: {
    label: string;
    value: string;
    hint?: string;
    icon: React.ComponentType<{ className?: string }>;
    tone: string;
    testId: string;
}) {
    return (
        <div
            data-testid={testId}
            className="rounded-2xl border border-border/70 bg-card p-5 shadow-soft"
        >
            <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-muted-foreground">{label}</span>
                <span className={`grid h-9 w-9 place-items-center rounded-xl ${tone}`}>
                    <Icon className="h-4 w-4" />
                </span>
            </div>
            <p className="mt-4 text-2xl font-extrabold text-foreground sm:text-3xl">{value}</p>
            {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
        </div>
    );
}

function StatusPill({ status }: { status: string }) {
    return (
        <span
            className={`inline-flex items-center rounded-full px-2 py-1 text-[11px] font-semibold uppercase tracking-wide ${
                STATUS_COLORS[status] || "bg-muted"
            }`}
        >
            {status.replace(/_/g, " ")}
        </span>
    );
}

const rupee = (n: number) => `₹${n.toLocaleString("en-IN")}`;

export default function AdminDashboard() {
    const { data, isLoading, isError, refetch } = useQuery<AdminStats>({
        queryKey: ["admin", "stats"],
        queryFn: adminApi.stats,
        refetchInterval: 15000,
    });

    return (
        <div data-testid="admin-dashboard-page" className="space-y-6">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-extrabold tracking-tight sm:text-3xl">
                        Dashboard
                    </h1>
                    <p className="text-sm text-muted-foreground">
                        Operations overview · live · updates every 15 s
                    </p>
                </div>
                <button
                    onClick={() => refetch()}
                    className="rounded-full border border-border px-3 py-1.5 text-xs font-semibold hover:bg-muted"
                    data-testid="admin-dashboard-refresh"
                >
                    Refresh now
                </button>
            </div>

            {isLoading ? (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    {[0, 1, 2, 3].map((i) => (
                        <div
                            key={i}
                            className="h-32 animate-pulse rounded-2xl border border-border/70 bg-muted/50"
                        />
                    ))}
                </div>
            ) : isError || !data ? (
                <div className="rounded-2xl border border-error/30 bg-error/5 p-6 text-center text-sm text-error">
                    Couldn't load dashboard stats — please try again.
                </div>
            ) : (
                <>
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        <StatCard
                            label="Total orders"
                            value={data.counts.orders.toLocaleString("en-IN")}
                            hint={`${data.counts.active_orders} active`}
                            icon={Package}
                            tone="bg-brand/10 text-brand"
                            testId="admin-stat-orders"
                        />
                        <StatCard
                            label="Revenue"
                            value={rupee(data.revenue.total)}
                            hint={`${rupee(data.revenue.today)} today`}
                            icon={IndianRupee}
                            tone="bg-emerald-500/10 text-emerald-600"
                            testId="admin-stat-revenue"
                        />
                        <StatCard
                            label="Customers"
                            value={data.counts.customers.toLocaleString("en-IN")}
                            hint="registered users"
                            icon={Users}
                            tone="bg-violet-500/10 text-violet-600"
                            testId="admin-stat-customers"
                        />
                        <StatCard
                            label="Products"
                            value={data.counts.products.toLocaleString("en-IN")}
                            hint={
                                data.counts.low_stock + data.counts.out_of_stock > 0
                                    ? `${data.counts.low_stock} low · ${data.counts.out_of_stock} out`
                                    : "all in stock"
                            }
                            icon={Boxes}
                            tone="bg-amber-500/10 text-amber-600"
                            testId="admin-stat-products"
                        />
                    </div>

                    <div className="grid gap-4 lg:grid-cols-3">
                        <div className="rounded-2xl border border-border/70 bg-card p-5 shadow-soft lg:col-span-2">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="text-lg font-bold">Revenue · last 30 days</h3>
                                    <p className="text-xs text-muted-foreground">
                                        7-day: {rupee(data.revenue.weekly)} · today:{" "}
                                        {data.revenue.today_orders} orders
                                    </p>
                                </div>
                                <TrendingUp className="h-5 w-5 text-brand" />
                            </div>
                            <div className="mt-4 h-56">
                                <ResponsiveContainer width="100%" height={220}>
                                    <AreaChart data={data.series}>
                                        <defs>
                                            <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor="#10b981" stopOpacity={0.35} />
                                                <stop offset="100%" stopColor="#10b981" stopOpacity={0.02} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                        <XAxis
                                            dataKey="date"
                                            tickFormatter={(v: string) => v.slice(5)}
                                            fontSize={11}
                                        />
                                        <YAxis
                                            tickFormatter={(v: number) => `₹${v}`}
                                            fontSize={11}
                                        />
                                        <Tooltip
                                            formatter={(v, name) => [
                                                name === "revenue" ? rupee(Number(v ?? 0)) : String(v ?? ""),
                                                String(name),
                                            ]}
                                        />
                                        <Area
                                            type="monotone"
                                            dataKey="revenue"
                                            stroke="#10b981"
                                            fill="url(#rev)"
                                            strokeWidth={2}
                                        />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        <div className="rounded-2xl border border-border/70 bg-card p-5 shadow-soft">
                            <h3 className="text-lg font-bold">Best sellers</h3>
                            <p className="text-xs text-muted-foreground">
                                By units sold, non-cancelled orders
                            </p>
                            <ul className="mt-4 space-y-3">
                                {data.best_sellers.length === 0 ? (
                                    <li className="text-sm text-muted-foreground">No orders yet.</li>
                                ) : (
                                    data.best_sellers.map((p, idx) => (
                                        <li
                                            key={p.product_id + idx}
                                            data-testid={`bestseller-${idx}`}
                                            className="flex items-center gap-3"
                                        >
                                            <span className="grid h-8 w-8 place-items-center rounded-lg bg-brand/10 text-xs font-bold text-brand">
                                                #{idx + 1}
                                            </span>
                                            <div className="min-w-0 flex-1">
                                                <p className="truncate text-sm font-medium">
                                                    {p.name}
                                                </p>
                                                <p className="text-xs text-muted-foreground">
                                                    {p.quantity} units · {rupee(p.revenue)}
                                                </p>
                                            </div>
                                        </li>
                                    ))
                                )}
                            </ul>
                        </div>
                    </div>

                    <div className="rounded-2xl border border-border/70 bg-card p-5 shadow-soft">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-bold">Recent orders</h3>
                                <p className="text-xs text-muted-foreground">
                                    Latest 5 orders across all customers
                                </p>
                            </div>
                            <Link
                                to={ROUTES.adminOrders}
                                className="inline-flex items-center gap-1 text-xs font-semibold text-brand hover:underline"
                            >
                                View all <ArrowUpRight className="h-3.5 w-3.5" />
                            </Link>
                        </div>
                        <div className="mt-4 overflow-x-auto">
                            <table className="min-w-full text-sm">
                                <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                                    <tr>
                                        <th className="py-2 pr-3">Order</th>
                                        <th className="py-2 pr-3">Status</th>
                                        <th className="py-2 pr-3">Total</th>
                                        <th className="py-2 pr-3">Placed</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border/60">
                                    {data.recent_orders.map((o) => (
                                        <tr key={o.id} data-testid={`recent-order-${o.id}`}>
                                            <td className="py-2.5 pr-3 font-semibold">
                                                <Link
                                                    to={ROUTES.adminOrderDetailBuild(o.id)}
                                                    className="hover:underline"
                                                >
                                                    {o.number}
                                                </Link>
                                            </td>
                                            <td className="py-2.5 pr-3">
                                                <StatusPill status={o.status} />
                                            </td>
                                            <td className="py-2.5 pr-3 font-semibold">
                                                {rupee(o.total)}
                                            </td>
                                            <td className="py-2.5 pr-3 text-xs text-muted-foreground">
                                                {new Date(o.placed_at).toLocaleString("en-IN", {
                                                    day: "2-digit",
                                                    month: "short",
                                                    hour: "2-digit",
                                                    minute: "2-digit",
                                                })}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
